<template>
  <div class="message-list-item-wrap">
    <div
      class="message-list-item"
      :class="{
        'is-selected': selected,
        'is-unread': conversation.unread > 0,
        'is-pinned': conversation.pinned,
        'is-muted': conversation.muted,
      }"
      @click="onSelect"
      @contextmenu="onContextmenu"
    >
      <z-avatar
        :size="40"
        :icon="avatarIcon"
        :style="{ backgroundColor: avatarColor }"
      />
      <div class="message-list-item__body">
        <div class="message-list-item__row">
          <span class="message-list-item__title">
            <z-icon v-if="conversation.pinned" class="message-list-item__flag">
              <Top />
            </z-icon>
            <span class="message-list-item__name">{{ conversation.title }}</span>
          </span>
          <z-icon v-if="conversation.starred" class="message-list-item__star">
            <Star />
          </z-icon>
          <z-icon v-if="conversation.muted" class="message-list-item__mute">
            <MuteNotification />
          </z-icon>
          <span class="message-list-item__time">{{ relativeTime }}</span>
        </div>
        <div class="message-list-item__row">
          <p class="message-list-item__preview">{{ preview }}</p>
          <span
            v-if="visibleLabels.length"
            class="message-list-item__labels"
          >
            <z-tag
              v-for="label in visibleLabels"
              :key="label"
              size="small"
              effect="plain"
            >
              {{ label }}
            </z-tag>
          </span>
          <span v-if="conversation.unread > 0" class="message-list-item__unread">
            {{ conversation.unread > 99 ? '99+' : conversation.unread }}
          </span>
        </div>
      </div>
    </div>
    <z-dropdown
      ref="dropdownRef"
      class="message-list-item-menu"
      :virtual-ref="triggerRef"
      virtual-triggering
      trigger="click"
      placement="bottom-start"
      :show-arrow="false"
      :popper-options="popperOptions"
      popper-class="message-list-item-popper"
      @command="onCommand"
    >
      <template #dropdown>
      <z-dropdown-menu>
        <z-dropdown-item
          command="unread"
          :icon="ChatDotSquare"
          :disabled="conversation.unread > 0"
        >
          标为未读
        </z-dropdown-item>
        <z-dropdown-item command="mute" :icon="MuteNotification">
          {{ conversation.muted ? '取消免打扰' : '消息免打扰' }}
        </z-dropdown-item>
        <z-dropdown-item command="pin" :icon="Top">
          {{ conversation.pinned ? '取消置顶' : '置顶' }}
        </z-dropdown-item>
        <z-dropdown-item divided command="star" :icon="Star">
          {{ conversation.starred ? '取消标记' : '标记' }}
        </z-dropdown-item>
        <z-dropdown-item command="label" :icon="PriceTag">
          添加标签
        </z-dropdown-item>
        <z-dropdown-item divided command="hide" :icon="Hide">
          不显示
        </z-dropdown-item>
        <z-dropdown-item
          v-if="canClear"
          divided
          command="clear"
          :icon="DocumentDelete"
        >
          清空聊天记录
        </z-dropdown-item>
        <z-dropdown-item
          :divided="!canClear"
          command="delete"
          :icon="Delete"
        >
          <span class="message-list-item-popper__danger">删除</span>
        </z-dropdown-item>
      </z-dropdown-menu>
      </template>
    </z-dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import {
  Bell,
  ChatDotRound,
  ChatDotSquare,
  Delete,
  DocumentDelete,
  Hide,
  MuteNotification,
  PriceTag,
  Star,
  Top,
  User,
  WarningFilled,
} from '@zoryn/icons'
import ZAvatar from '@zoryn/components/avatar'
import ZDropdown, {
  ZDropdownItem,
  ZDropdownMenu,
} from '@zoryn/components/dropdown'
import type { DropdownInstance } from '@zoryn/components/dropdown'
import ZIcon from '@zoryn/components/icon'
import ZTag from '@zoryn/components/tag'
import {
  TYPE_AVATAR_COLOR,
  type Conversation,
  type ConversationMenuCommand,
  type MessageType,
} from '../types'
import {
  closeExclusiveConversationMenu,
  openExclusiveConversationMenu,
} from '../utils/conversation-menu'
import { formatRelativeTime } from '../utils/message-format'
import '@zoryn/components/avatar/style'
import '@zoryn/components/dropdown/style'
import '@zoryn/components/icon/style'
import '@zoryn/components/tag/style'

const NOTICE_ICON: Record<MessageType, typeof Bell> = {
  system: Bell,
  business: ChatDotRound,
  alert: WarningFilled,
}

const props = defineProps<{
  conversation: Conversation
  selected?: boolean
}>()

const emit = defineEmits<{
  select: [id: string]
  command: [command: ConversationMenuCommand, id: string]
}>()

const avatarIcon = computed(() => {
  if (props.conversation.kind === 'direct') return User
  if (props.conversation.kind === 'group') return ChatDotRound
  return NOTICE_ICON[props.conversation.type ?? 'system']
})

const avatarColor = computed(() => {
  if (props.conversation.kind !== 'notice' && props.conversation.kind !== 'bot') {
    return 'var(--z-color-primary)'
  }
  return TYPE_AVATAR_COLOR[props.conversation.type ?? 'system']
})

const preview = computed(() => {
  const text = props.conversation.lastPreview
  if (!text) return '暂无消息'
  return text.length > 36 ? `${text.slice(0, 36)}…` : text
})

const relativeTime = computed(() =>
  formatRelativeTime(props.conversation.lastTime)
)

const canClear = computed(
  () =>
    props.conversation.kind === 'direct' || props.conversation.kind === 'group'
)

const visibleLabels = computed(() => (props.conversation.labels ?? []).slice(0, 2))

function cursorRect(x: number, y: number): DOMRect {
  return {
    x,
    y,
    width: 0,
    height: 0,
    top: y,
    left: x,
    right: x,
    bottom: y,
    toJSON: () => ({}),
  } as DOMRect
}

const dropdownRef = ref<DropdownInstance>()
const position = ref(cursorRect(0, 0))
const triggerRef = ref({
  getBoundingClientRect: () => position.value,
})
const popperOptions = {
  modifiers: [{ name: 'offset', options: { offset: [0, 0] } }],
}

function closeMenu() {
  dropdownRef.value?.handleClose()
}

function onSelect() {
  closeExclusiveConversationMenu()
  emit('select', props.conversation.id)
}

function onContextmenu(event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  position.value = cursorRect(event.clientX, event.clientY)
  openExclusiveConversationMenu(
    props.conversation.id,
    () => dropdownRef.value?.handleOpen(),
    closeMenu
  )
}

function onCommand(command: string | number | object) {
  emit('command', command as ConversationMenuCommand, props.conversation.id)
}

onBeforeUnmount(() => {
  closeExclusiveConversationMenu(props.conversation.id)
})
</script>
